import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { LazoChatConversation } from "@/models/ai-chat/LazoChatConversation";
import { SmallConversationMember } from "@/models/ai-chat/SmallConversationMember";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import { CloseOutlined } from "@mui/icons-material";
import { Box, Modal, Typography, styled } from "@mui/material";
import { useEffect, useState } from "react";
import LockLogoSVG from "../../../../assets/images/lock-logo.svg";
import UserAvatar from "../Avatar";

const StyledBox = styled(Box)(() => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  maxHeight: "258px",
  maxWidth: "381px",
  width: "100%",
  borderRadius: "1rem",
  backgroundColor: "white",
  padding: "24px",
  gap: "8px",
  border: `1px solid ${Colors.LIGHT_GRAY_656970}`,
  outline: "none",
}));

type UserListProps = {
  open: boolean;
  handleClose: () => void;
  conversation: LazoChatConversation;
};

function UserListModal(props: UserListProps) {
  const [userList, setUserList] = useState<SmallConversationMember[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hostExternalId, setHostExternalId] = useState<string>("");

  useEffect(() => {
    if (props.open) {
      setIsLoading(true);
      LazoChatConversationService.getChatMembers(props.conversation.externalId!)
        .then((r) => {
          const users = r.getContentOrThrowError();
          setUserList(users);
        })
        .catch((e: { message: string }) => NotificationService.error(e.message))
        .finally(() => setIsLoading(false));
      setHostExternalId(props.conversation.members.find((member) => member.isOwner)?.user.externalId!);
    }
  }, [props.open]);

  return (
    <Modal open={props.open} onClose={props.handleClose}>
      <StyledBox>
        <div style={{ position: "fixed", left: "89%", top: "9.2%" }} onClick={props.handleClose}>
          <CloseOutlined
            sx={{
              "&.MuiSvgIcon-root": { fontSize: "1.3rem" },
              ":hover": { cursor: "pointer" },
            }}
          />
        </div>
        <Box
          sx={{
            display: "flex",
            marginRight: "auto",
            justifyContent: "start",
            alignItems: "center",
            width: "90%",
            gap: "4px",
          }}
        >
          <img src={LockLogoSVG.src} width={"16px"} height={"16px"} />
          <Typography
            sx={{
              fontWeight: "500",
              fontSize: "14px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {props.conversation.title!}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", width: "100%" }}>
          <Typography sx={{ fontWeight: "500", fontSize: "12px" }}>Chat Members</Typography>
        </Box>
        <Box
          sx={{
            padding: "0.5rem 0",
            display: "flex",
            flexFlow: "column",
            width: "100%",
            gap: "0.6rem",
            overflowY: "auto",
          }}
        >
          {isLoading ? (
            <Spinner variant="indeterminate" sx={{ color: Colors.LAZO_PURPLE }} />
          ) : (
            userList.map((member, idx) => {
              return (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      flexFlow: "row",
                      alignItems: "center",
                      minHeight: "30px",
                      height: "30px",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexFlow: "row",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <UserAvatar
                        style={{
                          width: "32px",
                          height: "32px",
                          fontSize: "14px",
                        }}
                        key={idx}
                        user={
                          {
                            firstName: member.firstName,
                            lastName: member.lastName,
                          } as User
                        }
                      />
                      <Box>
                        <Typography sx={{ fontWeight: "500", fontSize: "12px" }}>{member.lastName + " " + member.firstName}</Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: `${Colors.STONE_GRAY}`,
                            fontWeight: "500",
                          }}
                        >
                          {member.role}
                        </Typography>
                      </Box>
                    </Box>
                    {member.userExternalId === hostExternalId && (
                      <Typography
                        sx={{
                          color: `${Colors.STONE_GRAY}`,
                          fontWeight: "500",
                          paddingRight: "1rem",
                        }}
                      >
                        Host
                      </Typography>
                    )}
                  </Box>
                  {idx !== userList.length - 1 && (
                    <div
                      style={{
                        height: "1px",
                        minHeight: "1px",
                        width: "100%",
                        backgroundColor: `${Colors.LIGHT_GRAY}`,
                      }}
                    />
                  )}
                </>
              );
            })
          )}
        </Box>
      </StyledBox>
    </Modal>
  );
}

export default UserListModal;
